import { Box, Skeleton, Text } from '@factorialco/deprecated-design-system'
import { F0Card } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

import { useParticipationData } from 'modules/engagement_surveys/hooks/useParticipationData'

interface Props {
  formId: string
  cycleIds: string[]
  filters: Record<string, unknown>
  formEndsAt?: string | null
  status?: string
  shouldSkip?: boolean
}

export function ParticipationCard({
  formId,
  cycleIds,
  filters,
  formEndsAt,
  status,
  shouldSkip = false,
}: Props) {
  const { sentCount, answeredCount, cycleNode, loading } = useParticipationData(
    formId,
    cycleIds,
    filters,
    shouldSkip
  )

  const cycleEndsAt = cycleNode?.endsAt || null
  const cycleStartsAt = cycleNode?.startsAt || null
  const daysToAnswer = cycleNode?.schedule?.daysToAnswer || null
  if (status && status !== 'active' && status !== 'finished') {
    return null
  }
  const participationPercentage = sentCount > 0 ? Math.round((answeredCount / sentCount) * 100) : 0

  // Calculate days left to finish
  const calculateDaysLeft = () => {
    const now = new Date()
    let endDate: Date | null = null

    // Priority 1: Calculate from startsAt + daysToAnswer (most accurate)
    if (cycleStartsAt && daysToAnswer) {
      const startDate = new Date(cycleStartsAt)
      endDate = new Date(startDate)
      endDate.setDate(endDate.getDate() + daysToAnswer)
    }
    // Priority 2: Use cycle endsAt as fallback
    else if (cycleEndsAt) {
      endDate = new Date(cycleEndsAt)
    }
    // Priority 3: Use form endsAt as last resort
    else if (formEndsAt) {
      endDate = new Date(formEndsAt)
    }

    // No deadline information available
    if (!endDate) {
      return i18n.t('surveys.participation_card.no_deadline')
    }

    const diffTime = endDate.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays < 0) {
      return i18n.t('surveys.participation_card.finished')
    }

    return i18n.tp('surveys.participation_card.days_left', { count: diffDays })
  }

  const daysLeftMessage = calculateDaysLeft()

  if (loading) {
    return (
      <Box width="full">
        <F0Card title={i18n.t('surveys.participation_card.title')}>
          <Box flexDirection="column" gap="s8" paddingX="s16" paddingY="s8">
            <Skeleton width="s96" height="s24" isLoading />
            <Skeleton width="s120" height="s16" isLoading />
          </Box>
        </F0Card>
      </Box>
    )
  }

  return (
    <Box width="full">
      <F0Card title={i18n.t('surveys.participation_card.title')}>
        <Box paddingX="s16" paddingY="s8" gap="s8" flexDirection="column">
          <Text size="500" weight="semibold" color="grey1000">
            {participationPercentage}%
          </Text>

          <Box flexDirection="row" alignItems="baseline" justifyContent="spaceBetween" width="full">
            <Text size="200" weight="normal" color="grey700">
              {answeredCount} / {sentCount} {i18n.t('surveys.participation_card.responses_label')}
            </Text>

            <Text size="200" weight="normal" color="grey600">
              {daysLeftMessage}
            </Text>
          </Box>
        </Box>
      </F0Card>
    </Box>
  )
}
